import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiPlay, FiVolume2, FiFileText, FiBookOpen, FiCalendar, 
  FiDownload, FiPlus, FiEdit, FiStar, 
  FiAlertCircle, FiUser, FiVideo,
  FiTrendingUp, FiChevronDown, FiClock, FiExternalLink,
  FiHardDrive, FiRefreshCcw
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import Notification from '../common/Notification';
import EditElectiveModal from '../admin/EditElectiveModal';
import AddContentModal from '../admin/AddContentModal';
import VideoPlaylist from '../courses/VideoPlaylist';
import VideoPlayer from '../courses/VideoPlayer';
import CourseAttendanceRecords from '../courses/CourseAttendanceRecords';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import richTextStyles from '../../styles/richTextViewer.module.css';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    max-width: 100%;
    margin: 0;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem;
    max-width: 100%;
    margin: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  max-width: 100%;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CourseCode = styled.div`
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  
  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  white-space: normal;
  min-width: 0;
  flex-shrink: 0;
  font-family: 'Inter', sans-serif;
  line-height: 1.2;
  
  /* Safari compatibility */
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  
  /* Cross-browser button reset */
  background: none;
  outline: none;
  text-decoration: none;
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-dark);
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: var(--text-primary);
    border: 1px solid var(--primary-color);
    
    &:hover {
      background-color: var(--primary-color);
      color: var(--text-inverse);
    }
  }
  
  /* Small size variant */
  &[data-size="sm"] {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    gap: 0.375rem;
    border-radius: 0;
    font-weight: 500;
    min-width: 0;
    
    @media (max-width: 768px) {
      padding: 0.5rem 0.75rem;
      font-size: 0.8rem;
      gap: 0.375rem;
      white-space: normal;
      min-width: 0;
      height: 2.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    @media (max-width: 480px) {
      padding: 0.5rem 0.75rem;
      font-size: 0.8rem;
      gap: 0.375rem;
      height: 2.25rem;
      min-width: fit-content;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.625rem 1rem;
    min-width: 0;
    flex: 1;
    white-space: normal;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
    gap: 0.375rem;
    height: 2.25rem;
    min-width: 0;
  }
`;

const VirtualLinkSection = styled(motion.div)`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
  
  /* Highlight effect when section is jumped to */
  ${props => props.$highlighted && `
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1), 0 6px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
    animation: highlightPulse 2s ease-in-out;
  `}
  
  @keyframes highlightPulse {
    0% {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2), 0 6px 12px rgba(0, 0, 0, 0.15);
    }
    50% {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1), 0 6px 12px rgba(0, 0, 0, 0.15);
    }
    100% {
      border-color: var(--border-color);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
  }
`;

const VirtualLinkHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    padding-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.375rem;
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
  }
`;

const VirtualLinkTitle = styled.h3`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    gap: 0.375rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
    gap: 0.25rem;
  }
`;

const VirtualLinkIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    svg {
      width: 20px;
      height: 20px;
    }
  }
  
  @media (max-width: 480px) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const VirtualLinkContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const VirtualLinkText = styled.p`
  color: var(--text-secondary);
  margin: 0;
  flex: 1 1 0;
  min-width: 0;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const JoinButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  text-decoration: none;
  border-radius: 0;
  font-weight: 500;
  font-size: 0.875rem;
  white-space: nowrap;
  transition: all 0.2s ease;
  min-width: fit-content;
  
  &:hover {
    background-color: var(--primary-hover);
    color: var(--text-inverse);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 0.875rem;
    gap: 0.5rem;
    width: auto;
    min-width: 200px;
    max-width: 280px;
    justify-content: center;
    align-self: center;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.8rem;
    gap: 0.375rem;
    min-width: 180px;
    max-width: 240px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: 1.25rem;
    margin-bottom: 1.25rem;
    max-width: 100%;
  }
`;


// Create a styled component without motion
const SectionBase = styled.div`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0;
    max-width: 100%;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    margin-bottom: 0;
    max-width: 100%;
    border-radius: 0;
  }
  
  /* Highlight effect when section is jumped to */
  ${props => props.$highlighted && `
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1), 0 6px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
    animation: highlightPulse 2s ease-in-out;
  `}
  
  @keyframes highlightPulse {
    0% {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2), 0 6px 12px rgba(0, 0, 0, 0.15);
    }
    50% {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1), 0 6px 12px rgba(0, 0, 0, 0.15);
    }
    100% {
      border-color: var(--border-color);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
  }
`;

// Create a motion wrapper component
const Section = React.forwardRef(({ children, $highlighted, ...motionProps }, ref) => {
  return (
    <motion.div ref={ref} {...motionProps}>
      <SectionBase $highlighted={$highlighted} className="course-section">
        {children}
      </SectionBase>
    </motion.div>
  );
});

const SectionHeader = styled.div.attrs({
  className: 'section-header'
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    gap: 0.5rem;
  }
`;

const SectionTitle = styled.h2.attrs({
  className: 'section-title'
})`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    gap: 0.375rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
    gap: 0.25rem;
  }
`;

const ClosePlayerButton = styled(ActionButton)`
  @media (max-width: 768px) {
    align-self: flex-end;
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const SectionIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    svg {
      width: 20px;
      height: 20px;
    }
  }
  
  @media (max-width: 480px) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const AddButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  @media (max-width: 768px) {
    padding: 0.375rem;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const ContentList = styled.div.attrs({
  className: 'content-list'
})`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
    max-width: 100%;
  }
`;

const ContentItem = styled.div.attrs({
  className: 'content-item'
})`
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-secondary);
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    max-width: 100%;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    padding: 0.625rem;
    max-width: 100%;
    border-radius: 0;
  }
`;

const ContentHeader = styled.div.attrs({
  className: 'content-header'
})`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-bottom: 0.375rem;
    max-width: 100%;
  }
`;

const ContentTitle = styled.h3.attrs({
  className: 'content-title'
})`
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  flex: 1 1 0;
  line-height: 1.4;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    width: 100%;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.3;
  }
`;

const FileTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0.25rem 0.6rem;
  border: 1px solid rgba(176, 141, 87, 0.28);
  border-radius: 999px;
  background: rgba(176, 141, 87, 0.08);
  color: var(--primary-color);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.2;
  white-space: nowrap;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
  width: 100%;
  justify-content: flex-start;
  box-sizing: border-box;
  align-items: flex-start;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
    align-items: flex-start;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    align-items: flex-start;
  }
`;

const ContentMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 0.375rem;
  }
  
  span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
    
    @media (max-width: 768px) {
      gap: 0.375rem;
    }
    
    @media (max-width: 480px) {
      gap: 0.25rem;
    }
  }
`;

const ContentDescription = styled.div`
  color: var(--text-primary);
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 1rem 0 0 0;
  padding: 0.75rem;
  background: var(--background-primary);
  border-radius: 0;
  border-left: 3px solid var(--primary-color);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.625rem;
    margin: 0.75rem 0 0 0;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.5rem;
    margin: 0.5rem 0 0 0;
  }

  /* Rich text content styling */
  p {
    margin: 0 0 1rem 0;
    line-height: 1.6;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 1rem 0 0.5rem 0;
    color: var(--text-primary);
    font-weight: 600;
    line-height: 1.3;
    
    &:first-child {
      margin-top: 0;
    }
  }

  h1 { font-size: 1.25rem; }
  h2 { font-size: 1.125rem; }
  h3 { font-size: 1rem; }
  h4 { font-size: 0.95rem; }
  h5 { font-size: 0.9rem; }
  h6 { font-size: 0.875rem; }

  ul, ol {
    margin: 0.75rem 0;
    padding-left: 1.5rem;
    
    li {
      margin: 0.25rem 0;
      line-height: 1.5;
    }
  }

  blockquote {
    margin: 1rem 0;
    padding: 0.75rem 1rem;
    border-left: 4px solid var(--primary-color);
    background: var(--background-secondary);
    border-radius: 0;
    font-style: italic;
  }

  code {
    background: var(--background-secondary);
    padding: 0.125rem 0.375rem;
    border-radius: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.875em;
    color: var(--text-primary);
  }

  pre {
    background: var(--background-secondary);
    padding: 1rem;
    border-radius: 0;
    overflow-x: auto;
    margin: 1rem 0;
    
    code {
      background: none;
      padding: 0;
      border-radius: 0;
    }
  }

  a {
    color: var(--primary-color);
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: all 0.2s ease;
    
    &:hover {
      color: var(--primary-hover);
      border-bottom-color: var(--primary-color);
      text-decoration: none;
    }
  }

  strong, b {
    font-weight: 600;
    color: var(--text-primary);
  }

  em, i {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }
`;

const SyllabusDescription = styled(ContentDescription)`
  font-size: 0.875rem;

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const PriorityBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  
  &.high {
    background-color: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
  }
  
  &.medium {
    background-color: rgba(246, 224, 94, 0.1);
    color: var(--warning-dark);
  }
  
  &.low {
    background-color: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
  }
`;

const PinnedBadge = styled.span`
  background-color: var(--primary-color);
  color: var(--text-inverse);
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const EmptyState = styled.div.attrs({
  className: 'empty-state'
})`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid var(--border-color);
  border-radius: 0;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const DropdownNavigation = styled(motion.div)`
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    gap: 0.5rem;
    border-radius: 0;
    flex-direction: row;
  }
`;

const SwitchCourseContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
  flex-shrink: 0;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-bottom: 0.75rem;
    justify-content: center;
    width: 100%;
    order: -1;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.5rem;
  }
`;

const SwitchCourseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.625rem;
  }
`;

const JumpToLabel = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 1rem;
  white-space: nowrap;
  margin-right: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: left;
    margin-right: 0.5rem;
    width: auto;
    flex-shrink: 0;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    text-align: left;
    margin-right: 0.5rem;
    width: auto;
    flex-shrink: 0;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  width: auto;
  min-width: 200px;
  max-width: 300px;
  flex-shrink: 0;
  box-sizing: border-box;
  
  /* Safari compatibility */
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    max-width: none;
    width: auto;
    min-width: 200px;
    flex: 1;
    display: block;
  }
  
  @media (max-width: 480px) {
    width: auto;
    min-width: 150px;
    flex: 1;
    display: block;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-secondary);
  color: var(--text-primary);
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  
  span {
    white-space: nowrap;
    flex: 0 1 auto;
    margin-right: 0.5rem;
  }
  
  &:hover {
    border-color: var(--primary-color);
    background-color: var(--background-primary);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  min-width: 250px;
  width: max-content;
  
  /* Custom scrollbar styling for better visibility */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--background-secondary);
    border-radius: 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 0;
    border: 1px solid var(--background-secondary);
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-muted);
  }
  
  /* Firefox scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: var(--border-color) var(--background-secondary);
  
  @media (max-width: 768px) {
    left: 0;
    right: 0;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    /* Make scrollbar more prominent on mobile */
    &::-webkit-scrollbar {
      width: 10px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: var(--primary-color);
      border-radius: 0;
    }
    
    scrollbar-width: auto;
    scrollbar-color: var(--primary-color) var(--background-secondary);
  }

  @media (max-width: 480px) {
    left: 0;
    right: 0;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: var(--text-primary);
  font-weight: 500;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  white-space: nowrap;
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  &:first-child {
    border-radius: 0;
  }
  
  &:last-child {
    border-radius: 0;
  }
  
  &:only-child {
    border-radius: 0;
  }
`;

const DropdownIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
`;

const DropdownLabel = styled.span`
  flex: 1;
  white-space: nowrap;
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

// BackToTopButton removed - using GlobalScrollToTop from App.js

function ElectiveDetail() {
  const { id: electiveId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [elective, setElective] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [syllabus, setSyllabus] = useState([]);
  const [slides, setSlides] = useState([]);
  const [readings, setReadings] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  // If the recording currently open in the player is removed (e.g. deleted from
  // the manage modal), close the player so it doesn't reference a missing video.
  useEffect(() => {
    if (selectedVideo && !(recordings || []).some(r => r.id === selectedVideo.id)) {
      setSelectedVideo(null);
    }
  }, [recordings, selectedVideo]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedSection, setHighlightedSection] = useState(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddContentModal, setShowAddContentModal] = useState(false);
  const [addContentModalTab, setAddContentModalTab] = useState('recordings');
  // showBackToTop removed - using GlobalScrollToTop from App.js

  // Refs for scrolling to sections
  const sectionRefs = useRef({});
  const dropdownRef = useRef(null);

  // Function to extract URLs from text (fallback for legacy content)
  const extractUrlsFromText = (text) => {
    if (!text) return [];
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex) || [];
    return urls.map((url, index) => ({
      url: url,
      label: 'Open Link' // Standardized fallback label
    }));
  };

  const handleEditElective = () => {
    setShowEditModal(true);
  };

  const handleManageContent = (tab = 'recordings') => {
    setAddContentModalTab(tab);
    setShowAddContentModal(true);
  };

  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      // Set highlighted section for visual feedback
      setHighlightedSection(sectionId);
      
      // Try scrollIntoView first (more reliable)
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
      
      // Fallback: manual scroll calculation
      setTimeout(() => {
        const elementRect = element.getBoundingClientRect();
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
        const absoluteElementTop = elementRect.top + currentScrollY;
        
        // Calculate offset to account for sticky header
        const isMobile = window.innerWidth <= 768;
        const headerHeight = isMobile ? 56 : 64;
        const offset = headerHeight + 20; // Extra 20px for breathing room
        const scrollPosition = absoluteElementTop - offset;
        
        // Only adjust if the element is not properly positioned
        if (Math.abs(elementRect.top - offset) > 50) {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
      
      // Remove highlight after animation completes
      setTimeout(() => {
        setHighlightedSection(null);
      }, 2000); // 2 seconds highlight duration
    }
  };

  const handleTabClick = (tabId) => {
    console.log('Tab clicked:', tabId);
    const tabs = getTabs();
    const tab = tabs.find(t => t.id === tabId);
    console.log('Found tab:', tab);
    
    // If it's a navigation link, navigate to the path
    if (tab?.isNavigation && tab?.path) {
      console.log('Navigating to:', tab.path);
      navigate(tab.path);
      return;
    }
    
    // Otherwise, handle as regular tab
    console.log('Setting active tab to:', tabId);
    setActiveTab(tabId);
    setIsDropdownOpen(false);
    
    // Add a delay to ensure DOM is updated before scrolling
    setTimeout(() => {
      console.log('Scrolling to section:', tabId);
      scrollToSection(tabId);
    }, 200);
  };

  const handleVideoSelect = (recording) => {
    setSelectedVideo(recording);
    
    // Scroll to the "Now Playing" section after a delay to ensure it's rendered
    setTimeout(() => {
      // Try multiple methods to find the Now Playing section
      let nowPlayingSection = document.querySelector('[data-section="now-playing"]');
      
      // Fallback: try to find by the "Now Playing" text
      if (!nowPlayingSection) {
        const sections = document.querySelectorAll('section');
        for (let section of sections) {
          const title = section.querySelector('h2');
          if (title && title.textContent.includes('Now Playing')) {
            nowPlayingSection = section;
            break;
          }
        }
      }
      
      console.log('Looking for Now Playing section:', nowPlayingSection);
      
      if (nowPlayingSection) {
        // Use the same reliable scroll method as scrollToSection
        nowPlayingSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
        
        // Fallback: manual scroll calculation with proper browser compatibility
        setTimeout(() => {
          const elementRect = nowPlayingSection.getBoundingClientRect();
          const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || window.scrollY || 0;
          const absoluteElementTop = elementRect.top + currentScrollY;
          
          // Calculate offset to account for sticky header
          const isMobile = window.innerWidth <= 768;
          const headerHeight = isMobile ? 56 : 64;
          const offset = headerHeight + 20; // Extra 20px for breathing room
          const scrollPosition = absoluteElementTop - offset;
          
          // Only adjust if the element is not properly positioned
          if (Math.abs(elementRect.top - offset) > 50) {
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      } else {
        console.warn('Now Playing section not found');
      }
    }, 400); // Increased delay to account for framer-motion animation (delay: 0.3s = 300ms)
  };

  // scrollToTop removed - using GlobalScrollToTop from App.js

  // handleScroll removed - using GlobalScrollToTop from App.js

  const toggleDropdown = () => {
    console.log('Dropdown toggle clicked, current state:', isDropdownOpen);
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getCurrentTabLabel = () => {
    const tabs = getTabs();
    const currentTab = tabs.find(tab => tab.id === activeTab);
    return currentTab ? currentTab.label : 'Select Section';
  };

  // Check if current user can add content to this elective
  const canAddContent = () => {
    if (!user || !elective) return false;
    
    // Admin can add content to any elective
    if (user.role === 'admin') return true;
    
    // Instructor can only add content to electives they're assigned to
    if (user.role === 'instructor') {
      // Check if user is one of the instructors in elective_instructors
      if (elective.instructors && Array.isArray(elective.instructors)) {
        return elective.instructors.some(inst => inst.id === user.id);
      }
      // Fallback to instructor_id for backward compatibility
      return elective.instructor_id === user.id;
    }
    
    return false;
  };

  const handleElectiveUpdated = (updatedElective) => {
    setElective(updatedElective);
    setShowEditModal(false);
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Success',
      message: 'Elective updated successfully!'
    });
  };


  const fetchElectiveData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch elective details first
      const electiveResponse = await apiService.getElective(electiveId);
      if (electiveResponse.success) {
        setElective(electiveResponse.elective);
      } else {
        throw new Error('Failed to fetch elective details');
      }
      
      // Fetch elective content
      try {
        const contentResponse = await apiService.getElectiveContent(electiveId);
        if (contentResponse.success) {
          setRecordings(contentResponse.content.recordings || []);
          setAnnouncements(contentResponse.content.announcements || []);
          setSyllabus(contentResponse.content.syllabus || []);
          setSlides(contentResponse.content.slides || []);
          setReadings(contentResponse.content.readings || []);
        }
      } catch (error) {
        console.log('Elective content not available:', error.message);
        // Set empty arrays as fallback
        setRecordings([]);
        setAnnouncements([]);
        setSyllabus([]);
        setSlides([]);
        setReadings([]);
      }
      
    } catch (error) {
      console.error('Error fetching elective data:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to fetch elective data'
      });
    } finally {
      setLoading(false);
    }
  }, [electiveId]);

  useEffect(() => {
    if (electiveId) {
      fetchElectiveData();
    }
  }, [electiveId, fetchElectiveData]);


  // Define tabs based on user role and available content
  const getTabs = useCallback(() => {
    const tabs = [];
    
    // Announcements tab - always available (moved to top)
    tabs.push({
      id: 'announcements',
      label: 'Announcements',
      icon: <FiAlertCircle size={16} />
    });
    
    // Virtual classroom tab - if link exists
    if (elective?.virtual_link) {
      tabs.push({
        id: 'virtual',
        label: 'Virtual Classroom',
        icon: <FiVideo size={16} />
      });
    }
    
    // Recordings tab - always available
    tabs.push({
      id: 'recordings',
      label: 'Recordings',
      icon: <FiVolume2 size={16} />
    });
    
    // Syllabus tab - always available
    tabs.push({
      id: 'syllabus',
      label: 'Syllabus',
      icon: <FiBookOpen size={16} />
    });
    
    // Slides tab - always available
    tabs.push({
      id: 'slides',
      label: 'Slides',
      icon: <FiFileText size={16} />
    });
    
    // Readings tab - always available
    tabs.push({
      id: 'readings',
      label: 'Readings',
      icon: <FiBookOpen size={16} />
    });
    
    
    // Attendance tab - only for students (moved to bottom)
    if (user?.role === 'student') {
      tabs.push({
        id: 'attendance',
        label: 'Attendance',
        icon: <FiTrendingUp size={16} />
      });
    }
    
    // Navigation links
    tabs.push({
      id: 'my-electives',
      label: 'My Electives',
      icon: <FiBookOpen size={16} />,
      isNavigation: true,
      path: '/electives'
    });
    
    if (user?.role === 'student') {
      tabs.push({
        id: 'my-assignments',
        label: 'My Assignments',
        icon: <FiFileText size={16} />,
        isNavigation: true,
        path: '/assignments'
      });
      
      tabs.push({
        id: 'my-quizzes',
        label: 'My Quizzes',
        icon: <FiEdit size={16} />,
        isNavigation: true,
        path: '/student/quizzes'
      });
    } else if (user?.role === 'instructor' || user?.role === 'admin') {
      tabs.push({
        id: 'assignments',
        label: 'Assignments',
        icon: <FiFileText size={16} />,
        isNavigation: true,
        path: '/assignments'
      });
      
      tabs.push({
        id: 'quizzes',
        label: 'Quizzes',
        icon: <FiEdit size={16} />,
        isNavigation: true,
        path: '/instructor/quizzes'
      });
    }
    
    return tabs;
  }, [user?.role, elective?.virtual_link]);

  // Set default active tab when elective data is loaded
  useEffect(() => {
    if (elective && !activeTab) {
      const tabs = getTabs();
      if (tabs.length > 0) {
        setActiveTab(tabs[0].id);
      }
    }
  }, [elective, activeTab, getTabs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Render the content-management modal BEFORE the loading gate. A background
  // refresh (e.g. after deleting a recording) flips `loading` true briefly; if
  // that swapped the modal for the spinner, the modal would unmount and lose its
  // success notification. Keeping it mounted lets the toast show and persist.
  if (showAddContentModal && elective) {
    return (
      <AddContentModal
        onClose={() => setShowAddContentModal(false)}
        elective={elective}
        initialTab={addContentModalTab}
        onContentAdded={(action) => {
          // Deletes show their own confirmation in the modal — just refresh the
          // elective data so the recordings playlist drops the removed video.
          if (action !== 'deleted') {
            setNotification({
              isOpen: true,
              type: 'success',
              title: 'Success',
              message: action === 'updated' ? 'Content updated successfully!' : 'Content added successfully!'
            });
          }
          // Refresh elective data to reflect the change (add/update/delete)
          fetchElectiveData();
        }}
      />
    );
  }

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <LoadingSpinner />
          <p>Loading elective content...</p>
        </div>
      </Container>
    );
  }

  if (!elective) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Elective not found</p>
        </div>
      </Container>
    );
  }

  const getInstructorNames = () => {
    if (elective?.instructors && Array.isArray(elective.instructors) && elective.instructors.length > 0) {
      return elective.instructors.map(inst => `${inst.first_name} ${inst.last_name}`).join(', ');
    }
    return `${elective?.instructor_first_name || ''} ${elective?.instructor_last_name || ''}`.trim() || 'N/A';
  };

  return (
    <Container className="elective-detail-container" role="main" aria-label="Elective details">
      <Header role="banner">
        <div>
          <Title>{elective.name}</Title>
          <CourseCode>
            <span>Elective ID: {elective.code}</span>
            <span style={{ fontWeight: 'normal' }}>
              Instructor{elective.instructors && elective.instructors.length > 1 ? 's' : ''}: {getInstructorNames()}
            </span>
          </CourseCode>
        </div>
        {canAddContent() && (
          <div style={{ 
            display: 'flex', 
            gap: '1rem',
            flexWrap: 'nowrap'
          }}>
            <ActionButton className="primary" onClick={handleEditElective}>
              <FiEdit size={16} />
              Edit Elective
            </ActionButton>
            <ActionButton className="primary" onClick={handleManageContent}>
              <FiPlus size={16} />
              Manage Content
            </ActionButton>
          </div>
        )}
      </Header>


      {/* Switch Elective Button - Outside the box, above on mobile */}
      <SwitchCourseContainer>
        <FiRefreshCcw size={16} color={'var(--primary-color)'} />
        <SwitchCourseButton
          type="button"
          onClick={() => navigate('/electives')}
        >
          Switch Elective
        </SwitchCourseButton>
      </SwitchCourseContainer>

      {/* Dropdown Navigation */}
      <DropdownNavigation
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        role="navigation"
        aria-label="Course sections navigation"
      >
        <JumpToLabel>Jump to:</JumpToLabel>
        <DropdownContainer ref={dropdownRef}>
          <DropdownButton onClick={toggleDropdown}>
            <span>{getCurrentTabLabel()}</span>
            <ChevronIcon $isOpen={isDropdownOpen}>
              <FiChevronDown size={16} />
            </ChevronIcon>
          </DropdownButton>
          <DropdownContent $isOpen={isDropdownOpen}>
            {getTabs().map((tab) => (
              <DropdownItem
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
              >
                <DropdownIcon>{tab.icon}</DropdownIcon>
                <DropdownLabel>{tab.label}</DropdownLabel>
              </DropdownItem>
            ))}
          </DropdownContent>
        </DropdownContainer>
      </DropdownNavigation>


      {/* Announcements Section moved to top */}
      <ContentGrid className="elective-content-grid" role="region" aria-label="Elective content">
        <Section
          ref={(el) => (sectionRefs.current['announcements'] = el)}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          $highlighted={highlightedSection === 'announcements'}
          role="region"
          aria-labelledby="announcements-heading"
        >
          <SectionHeader>
            <SectionTitle id="announcements-heading">
              <SectionIcon>
                <FiAlertCircle size={24} />
              </SectionIcon>
              Announcements
            </SectionTitle>
            {canAddContent() && (
              <AddButton 
                title="Add Announcement"
                onClick={() => handleManageContent('announcements')}
              >
                <FiPlus size={20} />
              </AddButton>
            )}
          </SectionHeader>
          {announcements.length === 0 ? (
            <EmptyState>
              <p>No announcements</p>
            </EmptyState>
          ) : (
            <ContentList>
              {announcements.map((announcement) => (
                <ContentItem key={announcement.id}>
                  <ContentHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', width: '100%', maxWidth: '100%' }}>
                      <ContentTitle>{announcement.title}</ContentTitle>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                        {announcement.is_pinned && (
                          <PinnedBadge>
                            <FiStar size={12} />
                            Pinned
                          </PinnedBadge>
                        )}
                        <PriorityBadge className={announcement.priority}>
                          {announcement.priority}
                        </PriorityBadge>
                      </div>
                    </div>
                  </ContentHeader>
                  <ContentMeta>
                    <span>
                      <FiCalendar size={14} />
                      {formatDateForDisplay(announcement.created_at, 'M/d/yyyy')}
                    </span>
                    <span>
                      <FiUser size={14} />
                      {announcement.creator_first_name} {announcement.creator_last_name}
                    </span>
                  </ContentMeta>
                  {(announcement.content && announcement.content.trim()) || (announcement.description && announcement.description.trim()) ? (
                    <ContentDescription
                      className={richTextStyles.richText}
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtmlForViewer(announcement.content || announcement.description) 
                      }}
                    />
                  ) : (
                    <ContentDescription style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
                      No content available
                    </ContentDescription>
                  )}
                </ContentItem>
              ))}
            </ContentList>
          )}
        </Section>
      </ContentGrid>

      {/* Virtual Link Section */}
      {elective.virtual_link && (
        <VirtualLinkSection
          ref={(el) => (sectionRefs.current['virtual'] = el)}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          $highlighted={highlightedSection === 'virtual'}
        >
          <VirtualLinkHeader>
            <VirtualLinkTitle>
              <VirtualLinkIcon>
                <FiVideo size={24} />
              </VirtualLinkIcon>
              Virtual Classroom
            </VirtualLinkTitle>
          </VirtualLinkHeader>
          <VirtualLinkContent>
            <VirtualLinkText>
              Join the live class session via Microsoft Teams
            </VirtualLinkText>
            <JoinButton 
              href={elective.virtual_link} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <FiVideo size={16} />
              Join Teams Meeting
            </JoinButton>
          </VirtualLinkContent>
        </VirtualLinkSection>
      )}

      <ContentGrid className="elective-content-grid">
        

        {/* Video Playlist Section */}
        <Section
          ref={(el) => (sectionRefs.current['recordings'] = el)}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ gridColumn: selectedVideo ? '1 / -1' : '1 / -1' }}
          $highlighted={highlightedSection === 'recordings'}
          role="region"
          aria-labelledby="recordings-heading"
        >
          <SectionHeader>
            <SectionTitle id="recordings-heading">
              <SectionIcon>
                <FiVolume2 size={24} />
              </SectionIcon>
              Elective Recordings
              {(() => {
                const readyCount = (recordings || []).filter(r => {
                  const status = r.processing_status
                    || r.video_variants?.processing_status
                    || 'ready';
                  const kind = r.processing_kind || r.video_variants?.kind || null;
                  return status === 'ready' && kind !== 'merge-source';
                }).length;
                if (readyCount === 0) return null;
                return (
                  <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                    ({readyCount} recording{readyCount !== 1 ? 's' : ''})
                  </span>
                );
              })()}
            </SectionTitle>
            {canAddContent() && (
              <AddButton
                title="Add Recording"
                onClick={() => handleManageContent('recordings')}
              >
                <FiPlus size={20} />
              </AddButton>
            )}
          </SectionHeader>

          <VideoPlaylist
            recordings={(recordings || []).filter(r => {
              // Hide rows that aren't fully converted yet from the playlist for
              // everyone — admins/instructors/granted users still see processing
              // items via Manage Course Content. Also drop transient merge-source
              // rows that aren't independently playable.
              const status = r.processing_status
                || r.video_variants?.processing_status
                || 'ready';
              const stage = r.processing_stage || r.video_variants?.stage || null;
              const kind = r.processing_kind || r.video_variants?.kind || null;
              return status === 'ready' && (!stage || stage === 'ready') && kind !== 'merge-source';
            })}
            onVideoSelect={handleVideoSelect}
            selectedVideo={selectedVideo}
          />
        </Section>

        {/* Video Player Section */}
        {selectedVideo && (
          <Section
            data-section="now-playing"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            style={{ gridColumn: '1 / -1' }}
          >
            <SectionHeader>
              <SectionTitle>
                <SectionIcon>
                  <FiPlay size={24} />
                </SectionIcon>
                Now Playing: {selectedVideo.title}
              </SectionTitle>
              <ClosePlayerButton 
                className="secondary" 
                onClick={() => setSelectedVideo(null)}
              >
                Close Player
              </ClosePlayerButton>
            </SectionHeader>
            <VideoPlayer 
              recording={selectedVideo} 
              autoPlay={true}
              onError={(error) => {
                setNotification({
                  isOpen: true,
                  type: 'error',
                  title: 'Video Error',
                  message: error
                });
              }}
            />
          </Section>
        )}


        {/* Syllabus Section */}
        <Section
          ref={(el) => (sectionRefs.current['syllabus'] = el)}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          $highlighted={highlightedSection === 'syllabus'}
        >
          <SectionHeader>
            <SectionTitle>
              <SectionIcon>
                <FiBookOpen size={24} />
              </SectionIcon>
              Syllabus
            </SectionTitle>
            {canAddContent() && (
              <AddButton 
                title="Add Syllabus Entry"
                onClick={() => handleManageContent('syllabus')}
              >
                <FiPlus size={20} />
              </AddButton>
            )}
          </SectionHeader>
          
          {syllabus.length === 0 ? (
            <EmptyState>
              <p>No syllabus available</p>
            </EmptyState>
          ) : (
            <ContentList>
              {syllabus.map((entry) => (
                <ContentItem key={entry.id}>
                  <ContentHeader>
                    <ContentTitle>{entry.title}</ContentTitle>
                  </ContentHeader>
                  <ContentMeta>
                    {entry.file_name && (
                      <span>
                        <FiFileText size={14} />
                        {entry.file_name}
                      </span>
                    )}
                    {entry.file_size && (
                      <span>
                        <FiHardDrive size={14} />
                        {(entry.file_size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    )}
                    <span>
                      <FiClock size={14} />
                      Posted: {formatDateForDisplay(entry.created_at, 'M/d/yyyy h:mm a')}
                    </span>
                  </ContentMeta>
                  {entry.description && entry.description.trim() && entry.description.trim() !== '<p></p>' && (
                    <SyllabusDescription
                      dangerouslySetInnerHTML={{ __html: sanitizeHtmlForViewer(entry.description) }}
                    />
                  )}
                  {entry.file_path && (
                    <ButtonContainer style={{ marginTop: '1rem' }}>
                      <a 
                        href={`/portal/api/course-files/${entry.file_path.split('/').pop()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          color: 'var(--primary-color)', 
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.375rem',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid var(--primary-color)',
                          borderRadius: 0,
                          transition: 'all 0.2s ease',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          fontFamily: 'Inter, sans-serif',
                          lineHeight: '1.2',
                          minWidth: 'fit-content',
                          height: '2.25rem',
                          backgroundColor: 'transparent'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = 'var(--primary-color)';
                          e.target.style.color = 'var(--text-inverse)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = 'var(--primary-color)';
                        }}
                      >
                        <FiFileText size={14} />
                        View PDF
                      </a>
                      <a 
                        href={`/portal/api/course-files/${entry.file_path.split('/').pop()}`}
                        download
                        style={{ 
                          color: 'var(--primary-color)', 
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.375rem',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid var(--primary-color)',
                          borderRadius: 0,
                          transition: 'all 0.2s ease',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          fontFamily: 'Inter, sans-serif',
                          lineHeight: '1.2',
                          minWidth: 'fit-content',
                          height: '2.25rem',
                          backgroundColor: 'transparent'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = 'var(--primary-color)';
                          e.target.style.color = 'var(--text-inverse)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = 'var(--primary-color)';
                        }}
                      >
                        <FiDownload size={14} />
                        Download
                      </a>
                    </ButtonContainer>
                  )}
                </ContentItem>
              ))}
            </ContentList>
          )}
        </Section>

        {/* Slides Section */}
        <Section
          ref={(el) => (sectionRefs.current['slides'] = el)}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          $highlighted={highlightedSection === 'slides'}
        >
          <SectionHeader>
            <SectionTitle>
              <SectionIcon>
                <FiFileText size={24} />
              </SectionIcon>
              Slides
            </SectionTitle>
            {canAddContent() && (
              <AddButton 
                title="Add Slide"
                onClick={() => handleManageContent('slides')}
              >
                <FiPlus size={20} />
              </AddButton>
            )}
          </SectionHeader>
          
          {slides.length === 0 ? (
            <EmptyState>
              <p>No slides available</p>
            </EmptyState>
          ) : (
            <ContentList>
              {slides.map((slide) => {
                const fileName = slide.file_name || slide.file_path?.split('/').pop() || '';
                const fileType = fileName.includes('.') ? fileName.split('.').pop().split('?')[0].split('#')[0].toUpperCase() : '';

                return (
                <ContentItem key={slide.id}>
                  <ContentHeader>
                    <ContentTitle>
                      {slide.title}
                    </ContentTitle>
                    {fileType && (
                      <FileTypeBadge>File type: {fileType}</FileTypeBadge>
                    )}
                  </ContentHeader>
                  {(slide.file_path || slide.url) && (
                    <ButtonContainer style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
                      {slide.file_path && (
                        <ActionButton 
                          className="secondary" 
                          data-size="sm"
                          onClick={() => window.open(`/portal/api/course-files/${slide.file_path.split('/').pop()}`, '_blank')}
                        >
                          <FiFileText size={12} />
                          View
                        </ActionButton>
                      )}
                      {slide.file_path && (
                        <ActionButton 
                          className="secondary" 
                          data-size="sm"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `/portal/api/course-files/${slide.file_path.split('/').pop()}`;
                            link.download = slide.file_name || 'slide';
                            link.click();
                          }}
                        >
                          <FiDownload size={12} />
                          Download
                        </ActionButton>
                      )}
                      {slide.url && (
                        <ActionButton 
                          className="secondary" 
                          data-size="sm"
                          onClick={() => window.open(slide.url, '_blank')}
                        >
                          <FiFileText size={12} />
                          Open Link
                        </ActionButton>
                      )}
                    </ButtonContainer>
                  )}
                  <ContentMeta>
                    {slide.week && (
                      <span>
                        <FiCalendar size={14} />
                        Week {slide.week}
                      </span>
                    )}
                    <span>
                      <FiClock size={14} />
                      Posted: {formatDateForDisplay(slide.created_at, 'M/d/yyyy h:mm a')} • <FiUser size={14} />
                      {slide.creator_first_name} {slide.creator_last_name}
                    </span>
                  </ContentMeta>
                  {slide.description && slide.description.trim() && slide.description.trim() !== '<p></p>' && (() => {
                    // Clean up any empty paragraphs, breaks, and whitespace
                    let cleanDescription = slide.description;
                    // Remove empty paragraphs with various whitespace patterns
                    cleanDescription = cleanDescription.replace(/<p>\s*<\/p>/gi, '');
                    cleanDescription = cleanDescription.replace(/<p>&nbsp;<\/p>/gi, '');
                    cleanDescription = cleanDescription.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '');
                    cleanDescription = cleanDescription.replace(/<p>\s*<br\s*\/?>\s*<br\s*\/?>\s*<\/p>/gi, '');
                    // Remove standalone breaks at the end
                    cleanDescription = cleanDescription.replace(/<br\s*\/?>\s*$/gi, '');
                    // Remove multiple consecutive breaks
                    cleanDescription = cleanDescription.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
                    // Remove leading/trailing breaks
                    cleanDescription = cleanDescription.replace(/^\s*<br\s*\/?>\s*/gi, '').replace(/\s*<br\s*\/?>\s*$/gi, '');
                    // Final trim
                    cleanDescription = cleanDescription.trim();
                    
                    const textContent = cleanDescription.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                    
                    return textContent ? (
                      <ContentDescription
                        className={richTextStyles.richText}
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeHtmlForViewer(cleanDescription) 
                        }}
                      />
                    ) : null;
                  })()}
                </ContentItem>
                );
              })}
            </ContentList>
          )}
        </Section>

        {/* Readings Section */}}
        <Section
          ref={(el) => (sectionRefs.current['readings'] = el)}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          $highlighted={highlightedSection === 'readings'}
        >
          <SectionHeader>
            <SectionTitle>
              <SectionIcon>
                <FiBookOpen size={24} />
              </SectionIcon>
              Readings
            </SectionTitle>
            {canAddContent() && (
              <AddButton 
                title="Add Reading"
                onClick={() => handleManageContent('readings')}
              >
                <FiPlus size={20} />
              </AddButton>
            )}
          </SectionHeader>
          
          {readings.length === 0 ? (
            <EmptyState>
              <p>No readings available</p>
            </EmptyState>
          ) : (
            <ContentList>
              {readings.map((reading) => (
                <ContentItem key={reading.id}>
                  <ContentHeader>
                    <ContentTitle>{reading.title}</ContentTitle>
                  </ContentHeader>
                  <ContentMeta>
                    {reading.week ? (
                      <span>
                        <FiCalendar size={14} />
                        Week {reading.week}
                      </span>
                    ) : (
                      <span>
                        <FiCalendar size={14} />
                        Posted: {formatDateForDisplay(reading.created_at, 'M/d/yyyy h:mm a')} • <FiUser size={14} />
                        {reading.creator_first_name} {reading.creator_last_name}
                      </span>
                    )}
                  </ContentMeta>
                  {(() => {
                    // STANDARDIZED READING LINK DISPLAY LOGIC
                    const allUrls = [];
                    const seenUrls = new Set();
                    const hasFile = reading.file_path && reading.file_path.trim();
                    
                    // Priority 1: Structured URLs from reading_urls table (always use "Open Link" label)
                    if (reading.urls && reading.urls.length > 0) {
                      reading.urls.forEach(urlItem => {
                        if (!seenUrls.has(urlItem.url)) {
                          // Always use "Open Link" label for consistency
                          allUrls.push({
                            url: urlItem.url,
                            label: 'Open Link'
                          });
                          seenUrls.add(urlItem.url);
                        }
                      });
                    }
                    // Priority 2: Legacy single URL from course_content table
                    else if (reading.url) {
                      if (!seenUrls.has(reading.url)) {
                        allUrls.push({ url: reading.url, label: 'Open Link' });
                        seenUrls.add(reading.url);
                      }
                    }
                    // Priority 3: URLs extracted from description text (only if no structured URLs exist)
                    else if (reading.description) {
                      const extractedUrls = extractUrlsFromText(reading.description);
                      extractedUrls.forEach(urlItem => {
                        if (!seenUrls.has(urlItem.url)) {
                          allUrls.push({ url: urlItem.url, label: 'Open Link' });
                          seenUrls.add(urlItem.url);
                        }
                      });
                    }
                    
                    // Render buttons if URLs exist or file_path exists
                    return (allUrls.length > 0 || hasFile) ? (
                      <ButtonContainer style={{ 
                        marginTop: '0.75rem',
                        marginBottom: '0.75rem'
                      }}>
                        {allUrls.map((urlItem, index) => (
                          <ActionButton 
                            key={`url-${index}`}
                            className="secondary" 
                            data-size="sm"
                            onClick={() => window.open(urlItem.url, '_blank')}
                          >
                            <FiExternalLink size={16} />
                            {urlItem.label}
                          </ActionButton>
                        ))}
                        {hasFile && (
                          <ActionButton 
                            key="file-path"
                            className="secondary" 
                            data-size="sm"
                            onClick={() => window.open(`/portal/api/course-files/${reading.file_path.split('/').pop()}`, '_blank')}
                          >
                            <FiFileText size={16} />
                            Open Link
                          </ActionButton>
                        )}
                      </ButtonContainer>
                    ) : null;
                  })()}
                  {reading.description && reading.description.trim() && reading.description.trim() !== '<p></p>' && (() => {
                    // Remove URLs from description since they're now rendered as buttons
                    let cleanDescription = reading.description;
                    const urlRegex = /(https?:\/\/[^\s]+)/g;
                    cleanDescription = cleanDescription.replace(urlRegex, '').trim();
                    
                    // Clean up any empty paragraphs, breaks, and whitespace
                    // Remove empty paragraphs with various whitespace patterns
                    cleanDescription = cleanDescription.replace(/<p>\s*<\/p>/gi, '');
                    cleanDescription = cleanDescription.replace(/<p>&nbsp;<\/p>/gi, '');
                    cleanDescription = cleanDescription.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '');
                    cleanDescription = cleanDescription.replace(/<p>\s*<br\s*\/?>\s*<br\s*\/?>\s*<\/p>/gi, '');
                    // Remove standalone breaks at the end
                    cleanDescription = cleanDescription.replace(/<br\s*\/?>\s*$/gi, '');
                    // Remove multiple consecutive breaks
                    cleanDescription = cleanDescription.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
                    // Remove leading/trailing breaks
                    cleanDescription = cleanDescription.replace(/^\s*<br\s*\/?>\s*/gi, '').replace(/\s*<br\s*\/?>\s*$/gi, '');
                    // Final trim
                    cleanDescription = cleanDescription.trim();
                    
                    // Check if there's any meaningful content left (not just empty HTML tags)
                    const textContent = cleanDescription.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                    
                    return textContent ? (
                      <ContentDescription
                        className={richTextStyles.richText}
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeHtmlForViewer(cleanDescription) 
                        }}
                      />
                    ) : null;
                  })()}
                </ContentItem>
              ))}
            </ContentList>
          )}
        </Section>

      </ContentGrid>

      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />

      {/* Edit Elective Modal */}
      {showEditModal && elective && (
        <EditElectiveModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          elective={elective}
          onElectiveUpdated={handleElectiveUpdated}
        />
      )}

      {/* AddContentModal is now rendered conditionally at the top of the component */}

      {/* Attendance Records Section - Only show for students (moved to bottom) */}

      {user?.role === 'student' && (
        <div style={{ marginBottom: '2rem' }}>
          <Section
            ref={(el) => (sectionRefs.current['attendance'] = el)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            $highlighted={highlightedSection === 'attendance'}
          >
            <CourseAttendanceRecords 
              electiveId={electiveId}
              electiveName={elective?.name}
              isElective={true}
            />
          </Section>
        </div>
      )}

      {/* Back to Top Button removed - using GlobalScrollToTop from App.js */}

    </Container>
  );
}

export default ElectiveDetail;
